import React from 'react';
import {FlatList, Image, Pressable, RefreshControl, StyleSheet, View} from 'react-native';
import Animated, {FadeIn, LayoutAnimationConfig} from 'react-native-reanimated';
import {Observer, observer} from 'mobx-react-lite';

import InStoreShoppingModal from '../../components/modals/InStoreShoppingModal';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AirtableBrand} from '../../models/airtable';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';

const FavouriteBrandsScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.FAVOURITE_BRANDS>) => {
    const {brandsStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();

    const onPress = (brand: AirtableBrand) => navigation.navigate(AppRoute.BRAND, {brand});

    const numColumns = isWebDesktopScreen ? 4 : 2;
    const renderTile = ({item, index}: {item: AirtableBrand; index: number}) => {
      const tileStyles = [styles.tile];

      if ((index + 1) % numColumns !== 0) {
        tileStyles.push(styles.leftTile);
      }

      return (
        <Animated.View entering={FadeIn.duration(300)} style={tileStyles} key={`${item.id}`}>
          <Pressable onPress={() => onPress(item)}>
            <View style={styles.innerView}>
              <Image
                style={styles.tileImage}
                source={{uri: item.logo[0].url}}
                resizeMode="contain"
              />
            </View>
          </Pressable>
        </Animated.View>
      );
    };

    return (
      <ContentView testID="favourite-brands-screen">
        <Observer>
          {() => (
            <LayoutAnimationConfig skipEntering>
              <FlatList
                id={`favourite-brands-flatlist-${numColumns}`}
                // slice(): [mobx] Out of bounds read: 17 at VirtualizedList (...) at FlatList (...)
                data={brandsStore.brands.slice()}
                keyExtractor={item => String(item.id)}
                renderItem={renderTile}
                numColumns={numColumns}
                style={styles.list}
                ItemSeparatorComponent={() => <View style={styles.separator} />}
                ListHeaderComponentStyle={styles.header}
                showsVerticalScrollIndicator={false}
                refreshControl={
                  <RefreshControl refreshing={false} onRefresh={() => brandsStore.loadBrands()} />
                }
              />
            </LayoutAnimationConfig>
          )}
        </Observer>
        <InStoreShoppingModal />
      </ContentView>
    );
  },
);

export default FavouriteBrandsScreen;

const styles = StyleSheet.create({
  list: {
    overflow: 'visible',
  },
  separator: {
    height: 0,
    borderTopWidth: 1,
    borderColor: 'lightgrey',
  },
  tile: {
    flex: 0.5,
    paddingVertical: 4,
    backgroundColor: 'white',
  },
  leftTile: {
    borderRightWidth: 0.5,
    borderRightColor: 'lightgrey',
  },
  innerView: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileImage: {
    width: '70%',
    aspectRatio: 1,
  },
  tileTextHolder: {
    paddingHorizontal: 4,
    marginVertical: 8,
  },
  tileText: {
    textAlign: 'center',
    fontSize: 16,
    fontWeight: '300',
    color: theme.colour.text.heading.primary,
    textTransform: 'uppercase',
  },
  header: {
    width: '100%',
  },
});
